import { useState, useEffect } from "react";
import { CaseServices } from "../../../services/CaseServices";
import { CircularProgress } from "@mui/material";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import EditIcon from "../../../assets/editIcon.svg";
import AddEnhancementForm from "../addEnhancement/AddEnhancementForm";
import { getUser } from "../../../config/user";
import "./CaseHistory.scss";

const CaseHistory = ({ caseDetails }) => {
  const [loading, setLoading] = useState(false);
  const [enhancementList, setEnhancementList] = useState([]);
  const [editEnhancement, setEditEnhancement] = useState(false);
  const user = getUser();

  const breakAfterNWords = (str, n = 5) => {
    if (!str) return "--";
    const words = str.split(" ");
    return words
      .reduce((acc, word, idx) => {
        acc += word + " ";
        if ((idx + 1) % n === 0 && idx !== words.length - 1) acc += "<br/>";
        return acc;
      }, "")
      .trim();
  };

  const daysOptions = {
    primary: [
      { value: "1", label: "1" },
      { value: "2", label: "2" },
    ],
    secondary: [
      { value: "1", label: "1" },
      { value: "2", label: "2" },
      { value: "3", label: "3" },
    ],
    tertiary: [
      { value: "1", label: "1" },
      { value: "2", label: "2" },
      { value: "3", label: "3" },
      { value: "4", label: "4" },
      { value: "5", label: "5" },
    ],
  };

  const getDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear(); // Removed hours and minutes
    return `${day}/${month}/${year}`;
  };

  const getAdmissionPackageCost = () => {
    return caseDetails?.caseprocedures?.reduce((prev, curr) => {
      return prev + Number(curr?.packageCost);
    }, 0);
  };

  const getEnhancementList = async () => {
    setLoading(true);
    try {
      const response = await CaseServices.getEnhancementsList(caseDetails.id);
      const procedureData = response.data.enhancements.map((enhancement) => {
        let data = caseDetails?.caseprocedures?.filter(
          (procedure) => procedure?.id === enhancement?.details?.procedureId,
        );
        if (data && data.length > 0) {
          return {
            ...enhancement,
            procedureCode: data[0].procedureId,
            procedure: data[0].procedure,
            speciality: data[0].speciality,
          };
        } else {
          return {
            ...enhancement,
            procedureCode: "",
            procedure: "",
            speciality: "",
          };
        }
      });
      response.data.enhancements = procedureData;
      setEnhancementList(response?.data?.enhancements);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getEnhancementList();
  }, []);

  console.log("enhancement list:", enhancementList);
  return (
    <div>
      <div className="font-semibold text-xl">Admission Details</div>
      <div
        style={{ border: "1px solid #f5f5f5" }}
        className="flex items-center gap-10 flex-wrap py-3 px-4 mt-4 rounded-[15px]"
      >
        <div className="font-semibold">
          Admission Date :{" "}
          <span className="font-normal">
            {getDate(caseDetails?.admissionDate)}
          </span>
        </div>
        <div className="font-semibold">
          Registered Date :{" "}
          <span className="font-normal">
            {getDate(caseDetails?.patient?.registeredDate)}
          </span>
        </div>
        <div className="font-semibold">
          Admission Type :{" "}
          <span className="font-normal">{caseDetails?.admissionType}</span>
        </div>
        <div className="font-semibold">
          Admission Cost :{" "}
          <span className="font-normal">₹ {getAdmissionPackageCost()}</span>
        </div>
      </div>
      <div className="font-semibold text-xl mt-8">Enhancement Details</div>
      <div
        style={{ border: "1px solid #f5f5f5" }}
        className="py-3 px-4 mt-4 rounded-[15px]"
      >
        {loading ? (
          <div className="flex justify-center mt-5 ">
            <CircularProgress />
          </div>
        ) : enhancementList && enhancementList.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <table className="min-w-full table-fixed border-separate border-spacing-y-2">
              <thead>
                <tr className="text-[#017C71] font-semibold whitespace-pre-line text-left">
                  <th className="w-10">No.</th>
                  {caseDetails?.isTpa !== true && (
                    <th className="w-28">Speciality</th>
                  )}
                  {caseDetails?.isTpa !== true && (
                    <th className="w-28">Procedure Code</th>
                  )}
                  <th className="w-48">Procedure</th>
                  <th className="w-40">Stratification</th>
                  <th className="w-20">
                    No. of Days/
                    <br />
                    Units
                  </th>
                  <th className="w-24">
                    Start
                    <br />
                    Date
                  </th>
                  <th className="w-24">
                    End
                    <br />
                    Date
                  </th>
                  {caseDetails?.isTpa !== true && (
                    <th className="w-28">
                      Enhancement
                      <br />
                      Cost
                    </th>
                  )}
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {enhancementList
                  ?.slice() // create shallow copy to avoid mutating original
                  .sort((a, b) => (a.sequence > b.sequence ? 1 : -1)) // sort in increasing order by sequence
                  .map((enhancement, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        idx % 2 === 0 ? "bg-[#F5F5F5]" : "bg-white"
                      } align-top`}
                      style={{ verticalAlign: "top" }}
                    >
                      <td className="break-words align-top">{idx + 1}</td>
                      {caseDetails?.isTpa !== true && (
                        <td className="break-words align-top">
                          {enhancement.speciality || "--"}
                        </td>
                      )}
                      {caseDetails?.isTpa !== true && (
                        <td className="break-words align-top">
                          {enhancement.procedureCode || "--"}
                        </td>
                      )}
                      <td
                        className="break-words align-top whitespace-pre-line"
                        dangerouslySetInnerHTML={{
                          __html: breakAfterNWords(
                            enhancement?.procedure ||
                              enhancement?.details?.procedure,
                            5,
                          ),
                        }}
                      />
                      <td
                        className="break-words align-top whitespace-pre-line"
                        dangerouslySetInnerHTML={{
                          __html: breakAfterNWords(
                            enhancement?.details?.wardType,
                            5,
                          ),
                        }}
                      />
                      <td className="break-words align-top">
                        {enhancement?.details?.enhancementDays || "--"}
                      </td>
                      <td className="break-words align-top">
                        {getDate(enhancement?.details?.fromDate) || "--"}
                      </td>
                      <td className="break-words align-top">
                        {getDate(enhancement?.details?.toDate) || "--"}
                      </td>
                      {caseDetails?.isTpa !== true && (
                        <td className="break-words align-top">
                          ₹ {enhancement?.details?.cost || "--"}
                        </td>
                      )}
                      <td
                        className="relative align-top"
                        style={{ minWidth: "40px" }}
                      >
                        {idx + 1 === enhancementList?.length &&
                          user?.roles?.filter(
                            (role) => role.roleId !== 1 && role.roleId !== 2,
                          )?.length > 0 &&
                          !caseDetails?.submasterstagestatuses[0]?.commandCenterStage
                            ?.toLowerCase()
                            .includes("documents  approved by team e") &&
                          !caseDetails?.submasterstagestatuses[0]?.commandCenterStage
                            ?.toLowerCase()
                            .includes("on tms") &&
                          !caseDetails?.submasterstagestatuses[0]?.commandCenterStage
                            ?.toLowerCase()
                            .includes("claim") &&
                          caseDetails?.isTpa !== true && (
                            <div
                              className="absolute top-[25%] right-1 cursor-pointer"
                              onClick={() =>
                                setEditEnhancement(!editEnhancement)
                              }
                            >
                              <img
                                src={EditIcon}
                                alt="edit-icon"
                                height={20}
                                width={20}
                              />
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-2xl font-semibold mt-5">
            No Data Found
          </div>
        )}
      </div>
      {editEnhancement && (
        <AddEnhancementForm
          daysOptions={daysOptions}
          caseProcedures={caseDetails?.caseprocedures}
          totalPackageCost={caseDetails.totalPackageCost}
          setAddEnhancement={setEditEnhancement}
          caseId={caseDetails.id}
          startDate={
            enhancementList.length > 0 && enhancementList[1]?.details?.toDate
              ? enhancementList[1]?.details?.toDate
              : caseDetails?.admissionDate
          }
          enhancementNumber={enhancementList.length + 1}
          caseDetails={caseDetails}
          type="Edit"
          enhancementDetails={enhancementList[0]}
          getEnhancementList={getEnhancementList}
        />
      )}
    </div>
  );
};

export default CaseHistory;
